import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { firestore, updateProfile } from '../config/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import Header from '../components/Header';
import './ProfilePage.css';

function ProfilePage() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userStats, setUserStats] = useState(null);

  useEffect(() => {
    if (currentUser) {
      setDisplayName(currentUser.displayName || '');
      fetchUserStats();
    } else {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  const fetchUserStats = async () => {
    try {
      const userDoc = await getDoc(doc(firestore, 'users', currentUser.uid));
      if (userDoc.exists()) {
        setUserStats(userDoc.data());
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      setError('Failed to log out');
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!displayName.trim()) {
      return setError('Display name cannot be empty');
    }

    try {
      setError('');
      setLoading(true);

      // Update display name in Auth
      await updateProfile(currentUser, {
        displayName: displayName
      });

      // Update display name in Firestore
      await updateDoc(doc(firestore, 'users', currentUser.uid), {
        displayName: displayName,
        updatedAt: new Date().toISOString()
      });

      setIsEditing(false);
      window.location.reload();
    } catch (error) {
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-container">
      <Header />
      <div className="profile-card">
        <h2>Profile</h2>
        {error && <div className="error-message">{error}</div>}
        
        <div className="profile-section">
          <div className="profile-header">
            <img 
              src={currentUser?.photoURL || '/assets/profile.webp'} 
              alt="Profile" 
              className="profile-picture"
            />
            {!isEditing ? (
              <h3>{currentUser?.displayName}</h3>
            ) : (
              <form onSubmit={handleUpdateProfile} className="edit-form">
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Display Name"
                  required
                />
                <div className="edit-buttons">
                  <button 
                    type="submit" 
                    className="save-button"
                    disabled={loading}
                  >
                    Save
                  </button>
                  <button 
                    type="button"
                    className="cancel-button"
                    onClick={() => {
                      setIsEditing(false);
                      setDisplayName(currentUser?.displayName || '');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>

          <div className="profile-info">
            <p><strong>Email:</strong> {currentUser?.email}</p>
            {userStats && (
              <>
                <p><strong>Member Since:</strong> {new Date(userStats.createdAt).toLocaleDateString()}</p>
                {/* Add more stats here as needed */}
              </>
            )}
          </div>

          <div className="profile-actions">
            {!isEditing && (
              <button 
                onClick={() => setIsEditing(true)}
                className="edit-button"
              >
                Edit Profile
              </button>
            )}
            <button 
              onClick={handleLogout}
              className="logout-button"
            >
              Log Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
